import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Mountain, Loader2, CheckCircle2, Store, Building2, UtensilsCrossed, Sparkles, ArrowLeft, ArrowRight,
  Hotel, Home, BedDouble, MapPin, ConciergeBell, CalendarDays, GraduationCap,
} from 'lucide-react';

const CATEGORIES = [
  { value: 'ecommerce', label: 'E-commerce', icon: Store, desc: 'Vendita prodotti online' },
  { value: 'hotel', label: 'Ospitalità', icon: Building2, desc: 'Hotel, B&B e case vacanze' },
  { value: 'restaurant', label: 'Ristorazione', icon: UtensilsCrossed, desc: 'Ristoranti e food' },
  { value: 'experience', label: 'Experience', icon: Sparkles, desc: 'Esperienze e attivita nel Parco' },
  { value: 'servizi', label: 'Servizi / Noleggi', icon: ConciergeBell, desc: 'Navetta, auto, legna, gas, assicurazioni…' },
  { value: 'eventi', label: 'Eventi', icon: CalendarDays, desc: 'Organizzazione e vendita di eventi' },
  { value: 'educational', label: 'Educational', icon: GraduationCap, desc: 'Corsi e formazione' },
];

// Sotto-tipologie per l'ospitalità (determina le categorie delle camere)
const HOSPITALITY_TYPES = [
  { value: 'hotel', label: 'Hotel', icon: Hotel, desc: 'Camere: Singola, Doppia, Suite…' },
  { value: 'casa_vacanze', label: 'Casa vacanze', icon: Home, desc: 'Appartamenti, Ville, Chalet…' },
  { value: 'bnb', label: 'B&B', icon: BedDouble, desc: 'L’host vive con gli ospiti' },
];

export default function RegisterPage() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = scelta attività/tipologia, 2 = dati account
  const [form, setForm] = useState({ name: '', email: '', password: '', company_name: '', phone: '', tenant_type: '', hospitality_category: '', locality: '' });
  const [localities, setLocalities] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/localities')
      .then((r) => { if (alive) setLocalities(r.data?.localities || []); })
      .catch(() => { if (alive) setLocalities([]); });
    return () => { alive = false; };
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isHotel = form.tenant_type === 'hotel';
  // Step 1 completo se: categoria scelta e, per l'ospitalità, anche la sotto-tipologia
  const step1Ready = form.tenant_type && (!isHotel || form.hospitality_category);

  const goStep2 = () => {
    setError('');
    if (!form.tenant_type) { setError('Seleziona una categoria.'); return; }
    if (isHotel && !form.hospitality_category) { setError('Scegli la tipologia della tua struttura.'); return; }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!acceptTerms) { setError('Devi accettare i Termini e Condizioni per registrarti.'); setLoading(false); return; }
      const payload = { ...form, accept_terms: true };
      if (!isHotel) delete payload.hospitality_category;
      const data = await register(payload);
      if (data?.status === 'verify_email') {
        navigate(`/verifica-email?email=${encodeURIComponent(form.email)}`);
        return;
      }
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#EFF6FF]">
            <CheckCircle2 className="h-7 w-7 text-primary" />
          </div>
          <h1 className="font-display text-xl font-bold">Richiesta inviata!</h1>
          <p className="mt-2 text-sm text-muted-foreground">Il tuo account e in attesa di approvazione da parte dell'amministratore. Riceverai accesso non appena verra approvato.</p>
          <Button className="mt-6 w-full" onClick={() => navigate('/login')} data-testid="register-goto-login">Torna al login</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="register-home-link"><ArrowLeft className="h-4 w-4" />Torna alla Home</Link>
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
          <div>
            <p className="font-display text-lg font-bold">Sila Experiences</p>
            <p className="text-xs text-muted-foreground">Registrazione stakeholder</p>
          </div>
        </div>
        <Card className="p-6 sm:p-8">
          {/* Indicatore passo */}
          <div className="mb-5 flex items-center gap-2" data-testid="register-steps">
            <span className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
            <span className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
          </div>

          {step === 1 && (
            <div data-testid="register-step-1">
              <h1 className="font-display text-2xl font-bold tracking-tight">Che attività gestisci?</h1>
              <p className="mt-1 text-sm text-muted-foreground">Seleziona la categoria per configurare gli strumenti giusti.</p>
              <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const active = form.tenant_type === c.value;
                  return (
                    <button type="button" key={c.value}
                      onClick={() => setForm({ ...form, tenant_type: c.value, hospitality_category: c.value === 'hotel' ? form.hospitality_category : '' })}
                      data-testid={`register-category-${c.value}`}
                      className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-center transition-colors ${active ? 'border-primary bg-accent' : 'border-border hover:bg-muted'}`}>
                      <Icon className={`h-5 w-5 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="text-xs font-medium leading-tight">{c.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Seconda scelta: tipologia struttura ospitalità */}
              {isHotel && (
                <div className="mt-6 rounded-xl border border-border bg-muted/30 p-4" data-testid="register-hospitality-block">
                  <p className="text-sm font-semibold text-foreground">Tipologia della struttura</p>
                  <p className="mb-3 text-xs text-muted-foreground">Determina le categorie delle camere/alloggi. La potrai cambiare nelle Impostazioni.</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {HOSPITALITY_TYPES.map((h) => {
                      const Icon = h.icon;
                      const active = form.hospitality_category === h.value;
                      return (
                        <button type="button" key={h.value} onClick={() => setForm({ ...form, hospitality_category: h.value })}
                          data-testid={`register-hospitality-${h.value}`}
                          className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${active ? 'border-primary bg-accent' : 'border-border bg-card hover:bg-muted'}`}>
                          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground"><Icon className={`h-4 w-4 ${active ? 'text-primary' : 'text-muted-foreground'}`} />{h.label}</span>
                          <span className="text-[11px] leading-tight text-muted-foreground">{h.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {error && <p className="mt-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</p>}
              <Button type="button" className="mt-6 w-full" disabled={!step1Ready} onClick={goStep2} data-testid="register-continue">
                Continua <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={submit} className="space-y-4" data-testid="register-step-2">
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight">I tuoi dati</h1>
                <p className="mt-1 text-sm text-muted-foreground">L'amministratore approverà la tua richiesta.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label htmlFor="name">Nome e cognome</Label><Input id="name" required value={form.name} onChange={upd('name')} data-testid="register-name-input" /></div>
                <div className="space-y-2"><Label htmlFor="company">Azienda</Label><Input id="company" required value={form.company_name} onChange={upd('company_name')} data-testid="register-company-input" /></div>
              </div>
              <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" autoComplete="username" required value={form.email} onChange={upd('email')} data-testid="register-email-input" /></div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" autoComplete="new-password" required minLength={15} value={form.password} onChange={upd('password')} placeholder="min 15 caratteri" data-testid="register-password-input" /></div>
                <div className="space-y-2"><Label htmlFor="phone">Telefono</Label><Input id="phone" value={form.phone} onChange={upd('phone')} data-testid="register-phone-input" /></div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="locality" className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> Località in Sila</Label>
                <Select value={form.locality} onValueChange={(v) => setForm({ ...form, locality: v })}>
                  <SelectTrigger id="locality" data-testid="register-locality-select"><SelectValue placeholder="Dove operi? (es. Lorica, Villaggio Palumbo…)" /></SelectTrigger>
                  <SelectContent>
                    {localities.map((l) => (
                      <SelectItem key={l} value={l} data-testid={`register-locality-option-${l}`}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Aiuta i visitatori a trovarti filtrando per zona nelle vetrine pubbliche.</p>
              </div>
              <label className="flex items-start gap-2.5 rounded-md border border-border bg-muted/20 p-3 text-sm" data-testid="register-terms-row">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} data-testid="register-accept-terms-checkbox" />
                <span className="text-muted-foreground">Accetto i <Link to="/termini" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline" data-testid="register-terms-link">Termini e Condizioni</Link> del servizio.</span>
              </label>
              {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</p>}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => { setError(''); setStep(1); }} data-testid="register-back">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Indietro
                </Button>
                <Button type="submit" className="flex-1" disabled={loading} data-testid="register-form-submit-button">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Invia richiesta
                </Button>
              </div>
            </form>
          )}

          <p className="mt-4 text-center text-sm text-muted-foreground">
            Hai gia un account? <Link to="/login" className="font-medium text-primary hover:underline">Accedi</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
